const DEFAULT_ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
];
const DEFAULT_HEARTBEAT_INTERVAL = 5000; // 5 seconds
const DEFAULT_HEARTBEAT_MISSED_THRESHOLD = 3; // 3 missed heartbeats = dead
const DEFAULT_MAX_ICE_RESTARTS = 3;
const DEFAULT_BUFFER_HIGH_WATER_MARK = 64 * 1024; // 64KB
const DEFAULT_BUFFER_LOW_WATER_MARK = 16 * 1024; // 16KB
const DEFAULT_MAX_QUEUE_SIZE = 100;
// Internal message types for heartbeat
const HEARTBEAT_PING = '__HEARTBEAT_PING__';
const HEARTBEAT_PONG = '__HEARTBEAT_PONG__';
/**
 * Manages a single WebRTC peer connection with a data channel.
 */
export class PeerConnection {
    pc;
    dataChannel = null;
    state = 'new';
    config;
    logger;
    pendingCandidates = [];
    iceRestartCount = 0;
    maxIceRestarts;
    bufferHighWaterMark;
    bufferLowWaterMark;
    maxQueueSize;
    messageQueue = [];
    isDraining = false;
    // Heartbeat state
    heartbeatIntervalId = null;
    lastPongTimestamp = 0;
    missedHeartbeats = 0;
    heartbeatInterval;
    heartbeatMissedThreshold;
    constructor(config) {
        this.config = config;
        this.logger = config.logger.child({ peerId: config.peerId });
        this.heartbeatInterval = config.heartbeatInterval ?? DEFAULT_HEARTBEAT_INTERVAL;
        this.heartbeatMissedThreshold =
            config.heartbeatMissedThreshold ?? DEFAULT_HEARTBEAT_MISSED_THRESHOLD;
        this.maxIceRestarts = config.maxIceRestarts ?? DEFAULT_MAX_ICE_RESTARTS;
        this.bufferHighWaterMark = config.bufferHighWaterMark ?? DEFAULT_BUFFER_HIGH_WATER_MARK;
        this.bufferLowWaterMark = config.bufferLowWaterMark ?? DEFAULT_BUFFER_LOW_WATER_MARK;
        this.maxQueueSize = config.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE;
        // Create peer connection
        this.pc = new RTCPeerConnection({
            iceServers: config.iceServers ?? DEFAULT_ICE_SERVERS,
        });
        this.setupEventHandlers();
        // If initiator, create the data channel
        if (config.isInitiator) {
            this.createDataChannel();
        }
    }
    setupEventHandlers() {
        // ICE gathering state changes
        this.pc.onicegatheringstatechange = () => {
            const gatheringState = this.pc.iceGatheringState;
            this.logger.debug({ gatheringState }, 'ICE gathering state changed');
        };
        // ICE candidate events
        this.pc.onicecandidate = (event) => {
            if (event.candidate) {
                // Log candidate type for debugging (host = local, srflx = STUN, relay = TURN)
                const candidateType = event.candidate.type ?? 'unknown';
                const address = event.candidate.address ?? 'unknown';
                this.logger.debug({ type: candidateType, address, candidate: event.candidate.candidate }, 'ICE candidate generated');
                this.config.onIceCandidate(event.candidate);
            }
            else {
                // Null candidate means ICE gathering is complete
                this.logger.debug('ICE gathering complete');
            }
        };
        // Connection state changes
        this.pc.onconnectionstatechange = () => {
            const pcState = this.pc.connectionState;
            this.logger.debug({ pcState }, 'Connection state changed');
            switch (pcState) {
                case 'connecting':
                    this.updateState('connecting');
                    break;
                case 'connected':
                    this.updateState('connected');
                    break;
                case 'disconnected':
                    this.updateState('disconnected');
                    break;
                case 'failed':
                    this.updateState('failed');
                    break;
                case 'closed':
                    this.updateState('closed');
                    break;
            }
        };
        // ICE connection state changes (more granular)
        this.pc.oniceconnectionstatechange = () => {
            const iceState = this.pc.iceConnectionState;
            this.logger.debug({ iceState }, 'ICE connection state changed');
            if (iceState === 'failed') {
                this.handleIceFailure();
            }
            else if (iceState === 'connected' || iceState === 'completed') {
                // Reset restart count on successful connection
                this.iceRestartCount = 0;
            }
        };
        // Data channel events (for non-initiator)
        this.pc.ondatachannel = (event) => {
            this.logger.debug('Received data channel');
            this.dataChannel = event.channel;
            this.setupDataChannelHandlers();
        };
    }
    createDataChannel() {
        this.logger.debug('Creating data channel');
        this.dataChannel = this.pc.createDataChannel('game', {
            ordered: true, // Reliable ordered delivery
        });
        this.setupDataChannelHandlers();
    }
    setupDataChannelHandlers() {
        if (!this.dataChannel)
            return;
        // Set the low water mark threshold for the bufferedamountlow event
        this.dataChannel.bufferedAmountLowThreshold = this.bufferLowWaterMark;
        this.dataChannel.onopen = () => {
            this.logger.info('Data channel opened');
            this.startHeartbeat();
        };
        this.dataChannel.onclose = () => {
            this.logger.info('Data channel closed');
            this.stopHeartbeat();
        };
        this.dataChannel.onerror = (error) => {
            this.logger.warn({ error }, 'Data channel error');
        };
        this.dataChannel.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                // Handle internal heartbeat messages
                if (message.type === HEARTBEAT_PING) {
                    this.handlePing(message.payload);
                    return;
                }
                if (message.type === HEARTBEAT_PONG) {
                    this.handlePong(message.payload);
                    return;
                }
                this.logger.trace({ type: message.type }, 'Received message via data channel');
                this.config.onMessage(message);
            }
            catch (err) {
                this.logger.error({ err, data: event.data }, 'Failed to parse data channel message');
            }
        };
        this.dataChannel.onbufferedamountlow = () => {
            this.logger.debug('Buffer amount low, draining queue');
            this.drainQueue();
        };
    }
    startHeartbeat() {
        this.stopHeartbeat(); // Clear any existing heartbeat
        this.lastPongTimestamp = Date.now();
        this.missedHeartbeats = 0;
        this.heartbeatIntervalId = setInterval(() => {
            this.checkHeartbeat();
        }, this.heartbeatInterval);
        this.logger.debug({ interval: this.heartbeatInterval }, 'Heartbeat started');
    }
    stopHeartbeat() {
        if (this.heartbeatIntervalId) {
            clearInterval(this.heartbeatIntervalId);
            this.heartbeatIntervalId = null;
            this.logger.debug('Heartbeat stopped');
        }
    }
    checkHeartbeat() {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
            this.stopHeartbeat();
            return;
        }
        // Check if we've received a pong since last check
        const timeSinceLastPong = Date.now() - this.lastPongTimestamp;
        if (timeSinceLastPong > this.heartbeatInterval) {
            this.missedHeartbeats++;
            this.logger.warn({ missedHeartbeats: this.missedHeartbeats, timeSinceLastPong }, 'Missed heartbeat response');
            if (this.missedHeartbeats >= this.heartbeatMissedThreshold) {
                this.logger.warn({ missedHeartbeats: this.missedHeartbeats }, 'Connection appears dead - too many missed heartbeats');
                this.stopHeartbeat();
                this.updateState('failed');
                return;
            }
        }
        // Send a ping
        this.sendInternal({ type: HEARTBEAT_PING, payload: Date.now() });
    }
    handlePing(timestamp) {
        // Respond to ping with pong
        this.sendInternal({ type: HEARTBEAT_PONG, payload: timestamp });
    }
    handlePong(timestamp) {
        const latency = Date.now() - timestamp;
        this.lastPongTimestamp = Date.now();
        this.missedHeartbeats = 0;
        this.logger.trace({ latency }, 'Received heartbeat pong');
    }
    /** Internal send that bypasses logging for heartbeat messages */
    sendInternal(message) {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
            return false;
        }
        try {
            this.dataChannel.send(JSON.stringify(message));
            return true;
        }
        catch {
            return false;
        }
    }
    updateState(newState) {
        if (this.state !== newState) {
            this.state = newState;
            this.config.onStateChange(newState);
        }
    }
    handleIceFailure() {
        this.iceRestartCount++;
        this.logger.warn({ attempt: this.iceRestartCount, maxAttempts: this.maxIceRestarts }, 'ICE connection failed');
        // IMMEDIATELY set state to 'disconnected' to trigger WebSocket fallback
        // This ensures messages don't get stuck trying to send via a broken WebRTC path
        // while we attempt ICE restarts in the background
        this.updateState('disconnected');
        if (this.iceRestartCount <= this.maxIceRestarts) {
            this.logger.info({ attempt: this.iceRestartCount }, 'Attempting ICE restart');
            if (this.config.onIceRestartNeeded) {
                this.config.onIceRestartNeeded();
            }
            else {
                this.pc.restartIce();
            }
        }
        else {
            this.logger.error({ attempts: this.iceRestartCount }, 'ICE restart failed after max attempts, falling back to WebSocket');
            this.updateState('failed');
            this.config.onIceRestartFailed?.();
        }
    }
    /** Create an SDP offer (initiator only) */
    async createOffer(options) {
        this.logger.debug('Creating offer');
        const offer = await this.pc.createOffer(options);
        await this.pc.setLocalDescription(offer);
        if (!offer.sdp) {
            throw new Error('Failed to create SDP offer');
        }
        return offer.sdp;
    }
    /** Create an SDP answer in response to an offer */
    async createAnswer(offerSdp) {
        this.logger.debug('Setting remote description and creating answer');
        await this.pc.setRemoteDescription({
            type: 'offer',
            sdp: offerSdp,
        });
        // Flush any candidates that arrived before remote description was set
        await this.flushPendingCandidates();
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        if (!answer.sdp) {
            throw new Error('Failed to create SDP answer');
        }
        return answer.sdp;
    }
    /** Set the remote answer (initiator only) */
    async setRemoteAnswer(answerSdp) {
        this.logger.debug('Setting remote answer');
        await this.pc.setRemoteDescription({
            type: 'answer',
            sdp: answerSdp,
        });
        // Flush any candidates that arrived before remote description was set
        await this.flushPendingCandidates();
    }
    /** Add an ICE candidate from the remote peer */
    async addIceCandidate(candidate, sdpMid, sdpMLineIndex) {
        // Buffer candidates until remote description is set
        if (!this.pc.remoteDescription) {
            this.logger.debug('Buffering ICE candidate (no remote description yet)');
            this.pendingCandidates.push({ candidate, sdpMid, sdpMLineIndex });
            return;
        }
        this.logger.debug('Adding ICE candidate');
        await this.pc.addIceCandidate({
            candidate,
            sdpMid,
            sdpMLineIndex,
        });
    }
    /** Flush any buffered ICE candidates after remote description is set */
    async flushPendingCandidates() {
        if (this.pendingCandidates.length === 0)
            return;
        this.logger.debug({ count: this.pendingCandidates.length }, 'Flushing buffered ICE candidates');
        const candidates = this.pendingCandidates;
        this.pendingCandidates = [];
        for (const { candidate, sdpMid, sdpMLineIndex } of candidates) {
            try {
                await this.pc.addIceCandidate({ candidate, sdpMid, sdpMLineIndex });
            }
            catch (err) {
                this.logger.error({ err, candidate }, 'Failed to add buffered ICE candidate');
            }
        }
    }
    /** Send a message over the data channel with backpressure handling */
    send(message) {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
            this.logger.warn({ state: this.dataChannel?.readyState }, 'Cannot send - data channel not open');
            return 'closed';
        }
        // If we have queued messages, queue this one too to preserve order
        if (this.messageQueue.length > 0) {
            return this.queueMessage(message);
        }
        // Check if buffer is too full
        if (this.dataChannel.bufferedAmount >= this.bufferHighWaterMark) {
            this.logger.debug({ bufferedAmount: this.dataChannel.bufferedAmount, threshold: this.bufferHighWaterMark }, 'Buffer full, queuing message');
            return this.queueMessage(message);
        }
        try {
            this.dataChannel.send(JSON.stringify(message));
            return 'sent';
        }
        catch (err) {
            this.logger.error({ err }, 'Failed to send message');
            return this.queueMessage(message);
        }
    }
    /** Queue a message when buffer is full */
    queueMessage(message) {
        if (this.messageQueue.length >= this.maxQueueSize) {
            this.logger.warn({ queueSize: this.messageQueue.length, maxSize: this.maxQueueSize, type: message.type }, 'Message queue overflow, triggering fallback');
            this.config.onBufferOverflow?.(message);
            return 'overflow';
        }
        this.messageQueue.push(message);
        this.logger.debug({ queueSize: this.messageQueue.length, type: message.type }, 'Message queued');
        return 'queued';
    }
    /** Drain the message queue when buffer has space */
    drainQueue() {
        if (this.isDraining || this.messageQueue.length === 0) {
            return;
        }
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
            return;
        }
        this.isDraining = true;
        try {
            while (this.messageQueue.length > 0) {
                // Stop if buffer is getting full again
                if (this.dataChannel.bufferedAmount >= this.bufferHighWaterMark) {
                    this.logger.debug({
                        remaining: this.messageQueue.length,
                        bufferedAmount: this.dataChannel.bufferedAmount,
                    }, 'Buffer full again, pausing drain');
                    break;
                }
                const message = this.messageQueue.shift();
                if (!message) {
                    break;
                }
                try {
                    this.dataChannel.send(JSON.stringify(message));
                }
                catch (err) {
                    // Re-queue at the front if send fails
                    this.messageQueue.unshift(message);
                    this.logger.error({ err }, 'Failed to send queued message');
                    break;
                }
            }
            if (this.messageQueue.length === 0) {
                this.logger.debug('Message queue drained');
            }
        }
        finally {
            this.isDraining = false;
        }
    }
    /** Get the current queue size */
    getQueueSize() {
        return this.messageQueue.length;
    }
    /** Get current connection state */
    getState() {
        return this.state;
    }
    /** Get selected ICE candidate pair info (if available) */
    async getSelectedCandidatePair() {
        const stats = await this.pc.getStats();
        const statsById = new Map();
        let selectedLocalId;
        let selectedRemoteId;
        let hasSelectedPair = false;
        stats.forEach((report) => {
            statsById.set(report.id, report);
            if (report.type !== 'candidate-pair')
                return;
            const pair = report;
            if (pair.selected) {
                selectedLocalId = pair.localCandidateId;
                selectedRemoteId = pair.remoteCandidateId;
                hasSelectedPair = true;
                return;
            }
            if (!hasSelectedPair && pair.nominated && pair.state === 'succeeded') {
                selectedLocalId = pair.localCandidateId;
                selectedRemoteId = pair.remoteCandidateId;
                hasSelectedPair = true;
            }
            else if (!hasSelectedPair && pair.state === 'succeeded') {
                selectedLocalId = pair.localCandidateId;
                selectedRemoteId = pair.remoteCandidateId;
                hasSelectedPair = true;
            }
        });
        if (!hasSelectedPair) {
            return null;
        }
        const localCandidate = selectedLocalId ? statsById.get(selectedLocalId) : undefined;
        const remoteCandidate = selectedRemoteId ? statsById.get(selectedRemoteId) : undefined;
        return {
            local: this.normalizeCandidate(localCandidate),
            remote: this.normalizeCandidate(remoteCandidate),
        };
    }
    /** Check if the connection is ready to send/receive */
    isConnected() {
        return this.state === 'connected' && this.dataChannel?.readyState === 'open';
    }
    /** Close the connection */
    close() {
        this.logger.debug('Closing peer connection');
        this.stopHeartbeat();
        this.dataChannel?.close();
        this.pc.close();
        this.updateState('closed');
    }
    normalizeCandidate(candidate) {
        if (!candidate) {
            return {};
        }
        const record = candidate;
        const address = typeof record['address'] === 'string'
            ? record['address']
            : typeof record['ip'] === 'string'
                ? record['ip']
                : undefined;
        return {
            candidateType: typeof record['candidateType'] === 'string' ? record['candidateType'] : undefined,
            address,
            protocol: typeof record['protocol'] === 'string' ? record['protocol'] : undefined,
            port: typeof record['port'] === 'number' ? record['port'] : undefined,
            relayProtocol: typeof record['relayProtocol'] === 'string' ? record['relayProtocol'] : undefined,
        };
    }
}
//# sourceMappingURL=peer-connection.js.map